<template>
  <div class="modal fade" id="transactionDetailsModal" tabindex="-1" aria-labelledby="transactionDetailsModalLabel" aria-hidden="true">
    <div class="modal-dialog modal-xl">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="transactionDetailsModalLabel">
            <i class="mdi mdi-receipt me-2"></i>Payroll Transaction Details
          </h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <div v-if="loading" class="text-center py-5">
            <div class="spinner-border text-primary me-2" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            <p class="mt-2">Loading transaction details...</p>
          </div>

          <div v-else-if="error" class="text-center py-5">
            <i class="mdi mdi-alert-circle-outline font-size-48 text-danger"></i>
            <h5 class="mt-2 text-danger">Failed to load transaction details</h5>
            <p class="text-muted">{{ error }}</p>
            <button @click="retryLoad" class="btn btn-primary mt-2">
              <i class="mdi mdi-refresh me-1"></i> Retry
            </button>
          </div>

          <div v-else-if="!transaction" class="text-center py-5">
            <i class="mdi mdi-alert-circle-outline font-size-48 text-muted"></i>
            <p class="mt-2">No transaction data available</p>
          </div>

          <div v-else>
            <!-- Employee and Period Information -->
            <div class="card mb-4">
              <div class="card-header bg-light">
                <h5 class="card-title mb-0">Employee & Period Information</h5>
              </div>
              <div class="card-body">
                <div class="row">
                  <div class="col-md-6">
                    <div class="d-flex align-items-center mb-3">
                      <div class="avatar-sm me-3 bg-primary rounded-circle d-flex align-items-center justify-content-center">
                        <i class="mdi mdi-account font-size-24 text-white"></i>
                      </div>
                      <div>
                        <h5 class="mb-0">{{ transaction.employee_name }}</h5>
                        <p class="text-muted mb-0">Employee ID: {{ transaction.employee_id }}</p>
                      </div>
                    </div>
                    <div class="table-responsive">
                      <table class="table table-sm table-borderless mb-0">
                        <tbody>
                        <tr>
                          <th style="width: 150px;">Department:</th>
                          <td>{{ transaction.department || 'N/A' }}</td>
                        </tr>
                        <tr>
                          <th>Transaction ID:</th>
                          <td>{{ transaction.id }}</td>
                        </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="table-responsive">
                      <table class="table table-sm table-borderless mb-0">
                        <tbody>
                        <tr>
                          <th style="width: 150px;">Payroll Period:</th>
                          <td>{{ transaction.period_name }}</td>
                        </tr>
                        <tr>
                          <th>Payment Status:</th>
                          <td>
                              <span class="badge" :class="getPaymentStatusBadgeClass(transaction.payment_status)">
                                {{ transaction.payment_status }}
                              </span>
                          </td>
                        </tr>
                        <tr>
                          <th>Payment Date:</th>
                          <td>{{ formatDate(transaction.payment_date) }}</td>
                        </tr>
                        <tr>
                          <th>Payment Reference:</th>
                          <td>{{ transaction.payment_reference || 'N/A' }}</td>
                        </tr>
                        <tr>
                          <th>Created By:</th>
                          <td>{{ transaction.created_by }}</td>
                        </tr>
                        <tr>
                          <th>Created At:</th>
                          <td>{{ formatDateTime(transaction.created_at) }}</td>
                        </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Salary Breakdown -->
            <div class="row mb-4">
              <!-- Earnings Section -->
              <div class="col-md-6">
                <div class="card h-100">
                  <div class="card-header bg-light">
                    <h5 class="card-title mb-0">
                      <i class="mdi mdi-cash-plus text-success me-2"></i>Earnings
                    </h5>
                  </div>
                  <div class="card-body">
                    <div class="table-responsive">
                      <table class="table table-sm mb-0">
                        <tbody>
                        <tr>
                          <td><strong>Basic Salary</strong></td>
                          <td class="text-end">{{ formatCurrency(transaction.basic_salary) }}</td>
                        </tr>

                        <!-- Allowances -->
                        <tr v-if="hasComponents('Allowance')" class="table-light">
                          <td colspan="2"><strong>Allowances</strong></td>
                        </tr>
                        <tr v-for="allowance in components.Allowance" :key="allowance.id">
                          <td class="ps-3">{{ allowance.component_name }}</td>
                          <td class="text-end text-success">{{ formatCurrency(allowance.amount) }}</td>
                        </tr>

                        <tr class="table-light fw-bold">
                          <td>Gross Salary</td>
                          <td class="text-end">{{ formatCurrency(transaction.gross_salary) }}</td>
                        </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Deductions Section -->
              <div class="col-md-6">
                <div class="card h-100">
                  <div class="card-header bg-light">
                    <h5 class="card-title mb-0">
                      <i class="mdi mdi-cash-minus text-danger me-2"></i>Deductions
                    </h5>
                  </div>
                  <div class="card-body">
                    <div class="table-responsive">
                      <table class="table table-sm mb-0">
                        <tbody>
                        <!-- Statutory Deductions -->
                        <tr v-if="hasComponents('Deduction')" class="table-light">
                          <td colspan="2"><strong>Statutory Deductions</strong></td>
                        </tr>
                        <tr v-for="deduction in components.Deduction" :key="deduction.id">
                          <td class="ps-3">{{ deduction.component_name }}</td>
                          <td class="text-end text-danger">{{ formatCurrency(deduction.amount) }}</td>
                        </tr>

                        <!-- Tax Components -->
                        <tr v-if="hasComponents('Tax')" class="table-light">
                          <td colspan="2"><strong>Tax</strong></td>
                        </tr>
                        <tr v-for="tax in components.Tax" :key="tax.id">
                          <td class="ps-3">{{ tax.component_name }}</td>
                          <td class="text-end text-danger">{{ formatCurrency(tax.amount) }}</td>
                        </tr>

                        <!-- Relief Components -->
                        <tr v-if="hasComponents('Relief')" class="table-light">
                          <td colspan="2"><strong>Reliefs</strong></td>
                        </tr>
                        <tr v-for="relief in components.Relief" :key="relief.id">
                          <td class="ps-3">{{ relief.component_name }}</td>
                          <td class="text-end text-success">{{ formatCurrency(Math.abs(relief.amount)) }}</td>
                        </tr>

                        <!-- Other Deductions -->
                        <tr v-if="hasComponents('Other')">
                          <td colspan="2"><strong>Other Deductions</strong></td>
                        </tr>
                        <tr v-for="other in components.Other" :key="other.id">
                          <td class="ps-3">{{ other.component_name }}</td>
                          <td class="text-end text-danger">{{ formatCurrency(other.amount) }}</td>
                        </tr>

                        <tr class="table-light fw-bold">
                          <td>Total Deductions</td>
                          <td class="text-end text-danger">{{ formatCurrency(transaction.total_deductions) }}</td>
                        </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Net Salary Summary -->
            <div class="card mb-4 border-primary">
              <div class="card-body">
                <div class="row align-items-center">
                  <div class="col-md-8">
                    <h4 class="mb-0">Net Salary</h4>
                    <p class="text-muted mb-0">Amount payable to employee</p>
                  </div>
                  <div class="col-md-4 text-md-end">
                    <h2 class="text-primary mb-0">{{ formatCurrency(transaction.net_salary) }}</h2>
                  </div>
                </div>
              </div>
            </div>

            <!-- Detailed Breakdown -->
            <div class="card">
              <div class="card-header bg-light">
                <h5 class="card-title mb-0">Calculation Summary</h5>
              </div>
              <div class="card-body">
                <div class="row">
                  <div class="col-md-6">
                    <table class="table table-sm table-borderless">
                      <tbody>
                      <tr>
                        <th>Basic Salary:</th>
                        <td class="text-end">{{ formatCurrency(transaction.basic_salary) }}</td>
                      </tr>
                      <tr>
                        <th>Total Allowances:</th>
                        <td class="text-end text-success">+ {{ formatCurrency(transaction.total_allowances) }}</td>
                      </tr>
                      <tr class="border-top">
                        <th>Gross Salary:</th>
                        <td class="text-end fw-bold">{{ formatCurrency(transaction.gross_salary) }}</td>
                      </tr>
                      </tbody>
                    </table>
                  </div>
                  <div class="col-md-6">
                    <table class="table table-sm table-borderless">
                      <tbody>
                      <tr>
                        <th>Taxable Income:</th>
                        <td class="text-end">{{ formatCurrency(transaction.taxable_income) }}</td>
                      </tr>
                      <tr>
                        <th>Income Tax (PAYE):</th>
                        <td class="text-end text-danger">- {{ formatCurrency(transaction.income_tax) }}</td>
                      </tr>
                      <tr>
                        <th>Total Deductions:</th>
                        <td class="text-end text-danger">- {{ formatCurrency(transaction.total_deductions) }}</td>
                      </tr>
                      <tr class="border-top">
                        <th>Net Salary:</th>
                        <td class="text-end fw-bold text-primary">{{ formatCurrency(transaction.net_salary) }}</td>
                      </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            <!-- Remarks -->
            <div v-if="transaction.remarks" class="card mt-4">
              <div class="card-header bg-light">
                <h5 class="card-title mb-0">Remarks</h5>
              </div>
              <div class="card-body">
                <p class="mb-0">{{ transaction.remarks }}</p>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer" v-if="getRight('MANAGE SALARY')">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
          <button
              v-if="transaction"
              type="button"
              class="btn btn-primary"
              @click="generatePayslip"
          >
            <i class="mdi mdi-file-document me-1"></i> Generate Payslip
          </button>
          <button
              v-if="transaction"
              type="button"
              class="btn btn-success"
              @click="viewEmployeeSalary"
          >
            <i class="mdi mdi-cash-multiple me-1"></i> Manage Salary
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import {authUrl, execute, getRight} from "@/api";

export default {
  name: 'TransactionDetails',
  props: {
    transactionId: {
      type: [String, Number],
      default: null
    }
  },
  data() {
    return {
      loading: false,
      error: null,
      transaction: null,
      components: {
        Allowance: [],
        Deduction: [],
        Tax: [],
        Relief: [],
        Other: []
      },
      modal: null,
      lastTransactionId: null
    };
  },
  mounted() {
    this.initModal();
  },
  methods: {
    getRight,
    initModal() {
      this.$nextTick(() => {
        this.modal = new bootstrap.Modal(document.getElementById('transactionDetailsModal'));

        document.getElementById('transactionDetailsModal').addEventListener('hidden.bs.modal', this.resetData);
      });
    },
    showModal(transactionId) {
      this.lastTransactionId = transactionId;
      this.fetchTransactionDetails(transactionId);

      if (this.modal) {
        this.modal.show();
      } else {
        this.initModal();
        setTimeout(() => {
          if (this.modal) this.modal.show();
        }, 100);
      }
    },
    hideModal() {
      if (this.modal) {
        this.modal.hide();
      }
    },
    resetData() {
      this.error = null;
      this.transaction = null;
      this.components = {
        Allowance: [],
        Deduction: [],
        Tax: [],
        Relief: [],
        Other: []
      };
    },
    retryLoad() {
      if (this.lastTransactionId) {
        this.fetchTransactionDetails(this.lastTransactionId);
      }
    },
    fetchTransactionDetails(transactionId) {
      this.loading = true;
      this.error = null;

      const data = new FormData();
      data.append("function", "getTransactionDetails");
      data.append("transactionId", transactionId);

      execute(data, authUrl)
          .then((res) => {
            this.loading = false;

            if (res.data && res.data.success) {
              this.transaction = res.data.data.transaction;

              // Process components
              if (this.transaction.components) {
                this.components = {
                  Allowance: this.transaction.components.Allowance || [],
                  Deduction: this.transaction.components.Deduction || [],
                  Tax: this.transaction.components.Tax || [],
                  Relief: this.transaction.components.Relief || [],
                  Other: this.transaction.components.Other || []
                };
              }
            } else {
              this.error = res.data?.message || "Failed to load transaction details";
            }
          })
          .catch((error) => {
            this.loading = false;
            console.error("API Error:", error);
            this.error = "An error occurred while loading transaction details. Please try again.";
          });
    },
    hasComponents(type) {
      return this.components[type] && this.components[type].length > 0;
    },
    formatCurrency(value) {
      return new Intl.NumberFormat('en-KE', {
        style: 'currency',
        currency: 'KES',
        minimumFractionDigits: 2
      }).format(value || 0);
    },
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      const options = { year: 'numeric', month: 'short', day: 'numeric' };
      return new Date(dateString).toLocaleDateString('en-US', options);
    },
    formatDateTime(dateTimeString) {
      if (!dateTimeString) return 'N/A';
      const options = {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      };
      return new Date(dateTimeString).toLocaleString('en-US', options);
    },
    getPaymentStatusBadgeClass(status) {
      switch (status) {
        case 'Pending':
          return 'bg-warning';
        case 'Processing':
          return 'bg-info';
        case 'Paid':
          return 'bg-success';
        case 'Failed':
          return 'bg-danger';
        case 'Cancelled':
          return 'bg-dark';
        default:
          return 'bg-secondary';
      }
    },
    generatePayslip() {
      // Open the payslip modal directly with the transaction ID
      this.$emit('generate-payslip', this.transaction.id);
    },
    viewEmployeeSalary() {
      this.$emit('view-employee-salary', this.transaction.employee_id);
    }
  }
};
</script>

<style scoped>
.avatar-sm {
  height: 3rem;
  width: 3rem;
}

.font-size-24 {
  font-size: 24px !important;
}

.font-size-48 {
  font-size: 48px !important;
}

.border-primary {
  border: 2px solid #4e73df !important;
}

.table-borderless th {
  color: #6c757d;
  font-weight: 500;
}
</style>